import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { DispatchProp, connect } from 'react-redux';
import React from 'react';
import { isEqual } from 'lodash';
import core, { App as AppTypes, Entities, Viewport } from '@gin-betting/core';
import banners, { BannerTypes } from '@gin-betting/banners';
import { BannerZoneLocation } from '@gin-betting/constants';

import { checkIfNewsPage } from 'utils';
import { withErrorBoundary } from 'hoc';
import { Widget, Helper, Portal } from 'components';
import { BettingWidgets, LocalRouts } from 'app-constants';
import {
    BetslipSidebarWrapper,
    HeaderMobile,
    LeftSidebar,
    OddsStatisticFetcher,
} from 'views';
import ui from 'modules/ui';
import { AsyncBetslipListSubscription } from 'modules/betslip/async';
import { AsyncMatch } from 'modules/match/async';
import { AsyncFreebetSelect } from 'modules/freebet/async';
import promo from 'modules/promo';
import { AsyncOutrightsBanners } from 'modules/outright/async';
import {
    initBettingReadyIndicator,
    withBettingReadyIndicatorContext,
    BettingReadyIndicatorContextProps,
} from 'modules/betting-ready-indicator';
import './app.scss';

const {
    entities: {
        actions: { getSports, getFreebets, getMarketTemplates },
    },
    viewport: {
        selectors: { makeViewportSelector },
    },
    utils: {
        getBettingOptions,
        isOneOfPages,
        checkOnRoute,
        getSportTypeFromLocation,
    },
    hocs: { withSubscription },
    constants: { isMobile },
} = core;

const {
    components: { BannersCarousel },
    selectors: { selectAreSportsAvailable },
} = promo;

const {
    components: { SettingsMenu },
} = ui;

const {
    actions: { getAvailableBannerZones },
} = banners;

interface MappedProps {
    greaterThan: Viewport.Breakpoints;
    lessThanOrEqual: Viewport.Breakpoints;
    areSportsAvailable: ReturnType<typeof selectAreSportsAvailable>;
}

interface MappedActions {
    getSports: Entities.GetSportsFn;
    getFreebets: typeof getFreebets;
    getAvailableBannerZones: typeof getAvailableBannerZones;
    getMarketTemplates: typeof getMarketTemplates;
}

type Props = MappedProps &
    MappedActions &
    RouteComponentProps<any> &
    DispatchProp<any> &
    BettingReadyIndicatorContextProps;

const UserBetSubscription = withSubscription(
    'App',
    'onUpdateBet',
    () => ({}), // there is no id for this subscription
    false
)(() => null);

class App extends React.Component<Props> {
    cachedSportTypes: string[] = [];
    prevSportType: AppTypes.SportType | undefined;

    constructor(props: Props) {
        super(props);

        this.state = {};
        this.prevSportType = getSportTypeFromLocation();

        // for sticky categorizer

        const layoutElement = document.querySelector('.layout');

        if (layoutElement) {
            (layoutElement as any).style.overflow = 'initial';
            (layoutElement as any).style.minWidth = 'initial';
        }

        const ineptWr = document.querySelector('.content-container-wrap');
        const ineptWr1 = document.querySelector(
            '.content-container-wrap .betting__wrapper'
        );

        if (ineptWr) {
            ineptWr.className = '';
        }

        if (ineptWr1) {
            ineptWr1.className = '';
        }

        // TODO: return me
        // this.updateBettingOptions(props.sportType);
    }

    componentDidMount() {
        const { sportIds, isAuthorized } = getBettingOptions();
        const { readyIndicator } = this.props;

        if (isAuthorized) {
            AsyncFreebetSelect.preload();
            this.props.getFreebets();
        }

        this.props.getSports({
            sportIds,
        });

        initBettingReadyIndicator(readyIndicator.initIndicator);

        setTimeout(() => {
            AsyncMatch.preload();
        }, 5000);

        this.props.getMarketTemplates({ marketTemplateIds: [], sportIds: [] });
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (
            !isEqual(window.__app.previousLocation, this.props.location) &&
            !isEqual(this.props.location, nextProps.location)
        ) {
            window.__app.previousLocation = this.props.location;
        }
    }

    componentDidUpdate() {
        const sportType = getSportTypeFromLocation();

        if (this.prevSportType !== sportType) {
            if (!this.cachedSportTypes.includes(sportType)) {
                this.cachedSportTypes.push(sportType);

                this.props.getAvailableBannerZones({
                    pageType: sportType,
                    platform: isMobile
                        ? BannerTypes.BannerZonePlatform.MOBILE
                        : BannerTypes.BannerZonePlatform.DESKTOP,
                });
            }

            this.prevSportType = sportType;
        }
    }

    render() {
        const {
            lessThanOrEqual,
            areSportsAvailable,
            location: { search },
        } = this.props;
        const { isAuthorized } = getBettingOptions();
        const isNewsPage = checkIfNewsPage();

        if (isNewsPage) {
            return (
                <div styleName="page-wrapper" className="__app-page-wrapper">
                    <div styleName="middle">{this.props.children}</div>
                </div>
            );
        }

        const isBannersCarouselVisible =
            lessThanOrEqual.small &&
            (isOneOfPages(
                [
                    AppTypes.Routes.UPCOMING_LIST,
                    AppTypes.Routes.ESPORT,
                    AppTypes.Routes.CLASSIC,
                ],
                { exact: true }
            ) ||
                isOneOfPages([
                    AppTypes.Routes.LIVE_LIST,
                    AppTypes.Routes.RESULTS_LIST,
                    AppTypes.Routes.PREMATCH,
                    (LocalRouts.FAVORITE_UPCOMING as unknown) as AppTypes.Routes,
                    (LocalRouts.FAVORITE_OUTRIGHTS as unknown) as AppTypes.Routes,
                    (LocalRouts.FAVORITE_RESULTS as unknown) as AppTypes.Routes,
                ]));

        const isOutrightsPage =
            checkOnRoute(AppTypes.Routes.OUTRIGHTS_LIST) && search === '';

        return (
            <React.Fragment>
                {isAuthorized && <UserBetSubscription />}
                <AsyncBetslipListSubscription />

                <Widget
                    widget={BettingWidgets.SETTINGS}
                    replacePortalContent={true}
                    isFilled={true}
                >
                    <SettingsMenu />
                </Widget>
                <Portal isPageDisabled={false}>
                    <Helper />
                </Portal>

                <HeaderMobile />

                <div styleName="page-wrapper" className="__app-page-wrapper">
                    <OddsStatisticFetcher />

                    {/* TODO: revise the logic of the left menu on the tablet.
                        AsyncCategorizerTabletContainer contains only categorizer */}
                    {/* {isTablet && <AsyncCategorizerTabletContainer />} */}

                    <Widget widget={BettingWidgets.CATEGORIZER}>
                        <LeftSidebar />
                    </Widget>

                    <div styleName="middle">
                        {isOutrightsPage && (
                            <AsyncOutrightsBanners
                                zoneLocation={
                                    BannerZoneLocation.TOP_OUTRIGHTS_SLIDER
                                }
                            />
                        )}

                        {isBannersCarouselVisible && areSportsAvailable && (
                            <BannersCarousel
                                zoneLocation={BannerZoneLocation.TOP_SLIDER}
                            />
                        )}

                        {this.props.children}
                    </div>

                    <BetslipSidebarWrapper />
                </div>
            </React.Fragment>
        );
    }
}

const mapStateToProps = createStructuredSelector<MappedProps>({
    greaterThan: makeViewportSelector('greaterThan'),
    lessThanOrEqual: makeViewportSelector('lessThanOrEqual'),
    areSportsAvailable: selectAreSportsAvailable,
});

const mapDispatchToProps: MappedActions = {
    getSports,
    getFreebets,
    getAvailableBannerZones,
    getMarketTemplates,
};

export default compose(
    withErrorBoundary,
    withRouter,
    connect<MappedProps, MappedActions, {}>(
        mapStateToProps,
        mapDispatchToProps
    ),
    withBettingReadyIndicatorContext
)(App);
